import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import chatStore from '../stores/chatStore';
import MessageBubble from './MessageBubble';
import EmptyState from './EmptyState';
import ScrollToBottom from './ScrollToBottom';

const MessageList = observer(() => {
    const scrollerRef = useRef(null);
    const contentRef = useRef(null);
    const bottomRef = useRef(null);

    useEffect(() => {
        if (!chatStore.stickToBottom) return;
        bottomRef.current?.scrollIntoView({ behavior: 'auto' });
    }, [chatStore.messages, chatStore.streamContent, chatStore.streaming, chatStore.stickToBottom]);

    useEffect(() => {
        const content = contentRef.current;
        if (!content || typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(() => {
            if (chatStore.stickToBottom) {
                bottomRef.current?.scrollIntoView({ behavior: 'auto' });
            }
        });
        observer.observe(content);
        return () => observer.disconnect();
    }, []);

    const onScroll = async () => {
        const el = scrollerRef.current;
        if (!el) return;
        const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
        chatStore.setStickToBottom(dist < 80);

        if (
            el.scrollTop < 160 &&
            chatStore.hasMoreMessages &&
            !chatStore.loadingOlderMessages
        ) {
            const previousHeight = el.scrollHeight;
            const previousTop = el.scrollTop;
            const loaded = await chatStore.loadOlderMessages();
            if (!loaded || !scrollerRef.current) return;
            requestAnimationFrame(() => {
                const current = scrollerRef.current;
                if (!current) return;
                current.scrollTop =
                    previousTop + (current.scrollHeight - previousHeight);
            });
        }
    };

    const scrollDown = () => {
        chatStore.setStickToBottom(true);
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const collapseSidebar = () => {
        if (!chatStore.sidebarCollapsed && !chatStore.sidebarPinned) {
            chatStore.setSidebarCollapsed(true);
        }
    };

    if (!chatStore.activeChatId && !chatStore.messages.length) {
        return <EmptyState />;
    }

    return (
        <div className="relative min-h-0 flex-1" onClick={collapseSidebar}>
            <div ref={scrollerRef} onScroll={onScroll} className="h-full overflow-y-auto px-3 py-6 sm:px-0">
                <div
                    ref={contentRef}
                    className="mx-auto flex w-full flex-col gap-6 sm:w-[70%]"
                >
                    {chatStore.loadingOlderMessages ? (
                        <div className="py-2 text-center text-xs opacity-50">
                            Загружаем ранние сообщения…
                        </div>
                    ) : null}
                    {chatStore.loadingMessages && !chatStore.messages.length ? (
                        <div className="py-10 text-center text-sm opacity-50">Загрузка…</div>
                    ) : null}
                    {chatStore.messages.map((m, index) => (
                        <MessageBubble
                            key={m.id}
                            message={m}
                            imagePriority={
                                index >= chatStore.messages.length - 8 ? 'high' : 'auto'
                            }
                        />
                    ))}
                    <div ref={bottomRef} />
                </div>
            </div>
            <ScrollToBottom onClick={scrollDown} />
        </div>
    );
});

export default MessageList;
